import React, { useState, useEffect, useRef } from 'react';
import {
	MagnifyingGlassIcon,
	DocumentIcon,
	PhotoIcon,
	ArrowUpTrayIcon,
	CheckIcon,
	EyeIcon,
} from '@heroicons/react/24/outline';

import { getDocuments } from '../api/documents';
import { uploadStepAttachment } from '../api/sops';
import { getModuleCategories } from '../api/modules';
import { StackableModal, List, FilterDropdown } from '../components/nextgen';
import { classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'documentPicker';
const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

// File type helpers
const getFileIcon = (contentType) => {
	if (contentType?.startsWith('image/')) return PhotoIcon;
	return DocumentIcon;
};

const formatFileSize = (bytes) => {
	if (!bytes) return '';
	const size = parseInt(bytes, 10);
	if (isNaN(size)) return bytes;
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString) => {
	if (!dateString) return '';
	const date = new Date(dateString);
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const DocumentPickerModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openDocumentPreview } = useModal('documentPreview');
	const fileInputRef = useRef(null);

	const [documents, setDocuments] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [selectedDocs, setSelectedDocs] = useState([]);
	const [uploading, setUploading] = useState(false);

	const multiSelect = open?.multiSelect ?? true;
	const maxSelect = open?.maxSelect ?? 10;

	// Fetch documents and categories
	useEffect(() => {
		if (open) {
			fetchDocuments();
			fetchCategories();
			setSelectedDocs([]);
			setSearch('');
			setSelectedCategory('all');
		}
	}, [open]);

	const fetchCategories = async () => {
		try {
			const result = await getModuleCategories({
				id: DOCUMENTS_MODULE_ID,
			});
			setCategories(result || []);
		} catch (error) {
			console.error('Failed to fetch categories:', error);
			setCategories([]);
		}
	};

	const fetchDocuments = async (categoryId = null) => {
		setLoading(true);
		try {
			const params = {
				pageSize: 50,
				sortBy: 'createdAt',
				sortOrder: 'DESC',
			};
			if (categoryId && categoryId !== 'all') {
				params.categoryId = categoryId;
			}
			const result = await getDocuments(params);
			setDocuments(result?.rows || result?.allRows || []);
		} catch (error) {
			console.error('Failed to fetch documents:', error);
			setDocuments([]);
		} finally {
			setLoading(false);
		}
	};

	const handleCategoryChange = (categoryId) => {
		setSelectedCategory(categoryId);
		fetchDocuments(categoryId);
	};

	const handleClose = () => {
		setSelectedDocs([]);
		setSearch('');
		setClose();
	};

	const handleSelect = () => {
		if (selectedDocs.length > 0 && typeof open?.onSelect === 'function') {
			open.onSelect(selectedDocs);
		}
		handleClose();
	};

	const toggleDocumentSelection = (doc) => {
		const isSelected = selectedDocs.some((d) => d.id === doc.id);

		if (isSelected) {
			setSelectedDocs(selectedDocs.filter((d) => d.id !== doc.id));
		} else {
			if (multiSelect) {
				if (selectedDocs.length < maxSelect) {
					setSelectedDocs([...selectedDocs, doc]);
				}
			} else {
				setSelectedDocs([doc]);
			}
		}
	};

	const handleFileUpload = async (e) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		setUploading(true);
		try {
			const uploadedDocs = [];
			for (const file of files) {
				const result = await uploadStepAttachment(file);
				uploadedDocs.push({
					id: result.id,
					documentId: result.documentId || result.id,
					name: result.filename || file.name,
					originalName: result.filename || file.name,
					size: result.size || file.size,
					contentType: result.mimeType || file.type,
					location: result.url,
					createdAt: new Date().toISOString(),
				});
			}

			// Refresh document list
			await fetchDocuments();

			// Auto-select newly uploaded documents
			setSelectedDocs((prev) => [...prev, ...uploadedDocs].slice(0, maxSelect));
		} catch (error) {
			console.error('Failed to upload file:', error);
		} finally {
			setUploading(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	};

	// Filter documents by search
	const filteredDocuments = documents.filter((doc) => {
		if (!search) return true;
		const searchLower = search.toLowerCase();
		return (
			doc.name?.toLowerCase().includes(searchLower) ||
			doc.originalName?.toLowerCase().includes(searchLower)
		);
	});

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title="Select Documents"
			size="2xl"
		>
			<div className="flex flex-col h-[500px]">
				{/* Search, Category Filter, and Upload Bar */}
				<div className="flex items-center gap-3 mb-4">
					<div className="relative flex-1">
						<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search documents..."
							className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
						/>
					</div>
					<FilterDropdown
						label="Category"
						placeholder="All Categories"
						value={selectedCategory}
						options={[
							{ value: 'all', label: 'All Categories' },
							{ value: 'uncategorized', label: 'Uncategorized' },
							...categories.map((cat) => ({
								value: cat.id,
								label: cat.name,
							})),
						]}
						onChange={handleCategoryChange}
					/>
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						disabled={uploading}
						className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
					>
						<ArrowUpTrayIcon className="w-4 h-4" />
						{uploading ? 'Uploading...' : 'Upload New'}
					</button>
					<input
						ref={fileInputRef}
						type="file"
						multiple
						accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
						onChange={handleFileUpload}
						className="hidden"
					/>
				</div>

				{/* Selection Info */}
				{selectedDocs.length > 0 && (
					<div className="flex items-center justify-between px-3 py-2 mb-3 bg-indigo-50 border border-indigo-100 rounded-lg">
						<span className="text-sm text-indigo-700">
							{selectedDocs.length} document
							{selectedDocs.length !== 1 ? 's' : ''} selected
						</span>
						<button
							type="button"
							onClick={() => setSelectedDocs([])}
							className="text-sm text-indigo-600 hover:text-indigo-800"
						>
							Clear selection
						</button>
					</div>
				)}

				{/* Document List */}
				<div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
					<List
						data={filteredDocuments}
						loading={loading}
						keyField="id"
						bordered={false}
						emptyTitle="No documents found"
						emptyDescription={
							search
								? 'Try a different search term'
								: 'Upload a document to get started'
						}
						renderItem={(doc) => {
							const FileIcon = getFileIcon(doc.contentType);
							const isSelected = selectedDocs.some((d) => d.id === doc.id);
							const isImage = doc.contentType?.startsWith('image/');

							return (
								<div
									onClick={() => toggleDocumentSelection(doc)}
									className={classNames(
										'flex items-center gap-3 cursor-pointer -mx-4 -my-4 px-4 py-3',
										isSelected ? 'bg-indigo-50' : '',
									)}
								>
									{/* Thumbnail/Icon - clickable for preview */}
									<div
										className="flex-shrink-0 cursor-pointer hover:opacity-75 transition-opacity"
										onClick={(e) => {
											e.stopPropagation();
											openDocumentPreview(doc);
										}}
										title="Click to preview"
									>
										{isImage && doc.location ? (
											<img
												src={doc.location}
												alt={doc.name}
												className="w-10 h-10 object-cover rounded"
											/>
										) : (
											<div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded">
												<FileIcon className="w-5 h-5 text-gray-500" />
											</div>
										)}
									</div>

									{/* Document Info */}
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">
											{doc.name || doc.originalName}
										</p>
										<p className="text-xs text-gray-500">
											{formatFileSize(doc.size)}
											{doc.category?.name && ` • ${doc.category?.name}`}

											{doc.createdAt && ` • ${formatDate(doc.createdAt)}`}
										</p>
									</div>

									{/* Preview Button */}
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											openDocumentPreview(doc);
										}}
										className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
										title="Preview"
									>
										<EyeIcon className="w-4 h-4" />
									</button>

									{/* Selection Indicator */}
									<div className="flex-shrink-0">
										{isSelected ? (
											<div className="w-5 h-5 flex items-center justify-center bg-indigo-600 rounded-full">
												<CheckIcon className="w-3 h-3 text-white" />
											</div>
										) : (
											<div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
										)}
									</div>
								</div>
							);
						}}
					/>
				</div>

				{/* Footer Actions */}
				<div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleSelect}
						disabled={selectedDocs.length === 0}
						className={classNames(
							'px-4 py-2 text-sm font-medium rounded-lg',
							selectedDocs.length > 0
								? 'bg-indigo-600 text-white hover:bg-indigo-700'
								: 'bg-gray-100 text-gray-400 cursor-not-allowed',
						)}
					>
						Select {selectedDocs.length > 0 ? `(${selectedDocs.length})` : ''}
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default DocumentPickerModal;
